import * as React from "react";
import {
  CircularProgress,
  FormControl,
  MenuItem,
  TextField,
  Typography
} from "@mui/material";
import { Stack } from "@mui/system";
import { Controller, useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { EmailRegex, companySizeOptions } from "utils/constants";
import { CustomButton } from "components/SharedComponents/CustomButton";
import { ToastOptions, toast } from "react-toastify";
import { IRequestDemoParams, requestDemo } from "network/apis/demo";

type IDemoRequestForm = {
  handleClose: () => void;
};

const defaultValues: IRequestDemoParams = {
  firstName: "",
  lastName: "",
  email: "",
  companyName: "",
  companySize: ""
};

const toastOptions: ToastOptions = {
  position: "top-center",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
  theme: "light"
};

export default function DemoRequestForm({ handleClose }: IDemoRequestForm) {
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    control,
    formState: { errors, isValid },
    getValues,
    trigger
  } = useForm({
    mode: "onChange",
    defaultValues
  });

  const handleFormSubmit = async () => {
    const currentValues = getValues();

    trigger();
    if (isValid) {
      setIsLoading(true);

      requestDemo(currentValues)
        .then(() => {
          handleClose();
          toast.success(
            "Your information is sent to us and we will contact you very soon!",
            toastOptions
          );
        })
        .catch((error) => {
          toast.error(
            "Something went wrong, please try again later!",
            toastOptions
          );
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Stack
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 400,
        bgcolor: "background.paper",
        boxShadow: 24,
        p: 4
      }}
      gap={2}
    >
      <Typography
        id="modal-modal-title"
        variant="h6"
        component="h2"
        sx={{ alignSelf: "center" }}
      >
        Request a Demo Now!
      </Typography>
      <Typography id="modal-modal-description" sx={{ mt: 2 }}>
        Fill this form with your information and we will contact you very soon.
      </Typography>
      <Stack gap={2}>
        <Controller
          control={control}
          name="firstName"
          rules={{
            required: "First name is required"
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl>
              <TextField
                id="firstName"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                error={error?.message ? true : false}
                title="First Name"
                variant="outlined"
                label="First name"
                sx={{
                  width: "100%"
                }}
              />
              <ErrorMessage
                style={{
                  color: "red",
                  fontSize: "0.85rem",
                  marginTop: "0.375rem"
                }}
                errors={errors}
                name="firstName"
                as="p"
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="lastName"
          rules={{
            required: "Last name is required"
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl>
              <TextField
                id="lastName"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                error={error?.message ? true : false}
                title="Last Name"
                variant="outlined"
                label="Last name"
                sx={{
                  width: "100%"
                }}
              />
              <ErrorMessage
                style={{
                  color: "red",
                  fontSize: "0.85rem",
                  marginTop: "0.375rem"
                }}
                errors={errors}
                name="lastName"
                as="p"
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="email"
          rules={{
            required: "Work email is required",
            pattern: {
              value: EmailRegex,
              message: "This Email is invalid"
            }
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl>
              <TextField
                id="email"
                type="email"
                name="email"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                error={error?.message ? true : false}
                title="Your company email"
                variant="outlined"
                label="Work email"
                sx={{
                  width: "100%"
                }}
              />
              <ErrorMessage
                style={{
                  color: "red",
                  fontSize: "0.85rem",
                  marginTop: "0.375rem"
                }}
                errors={errors}
                name="email"
                as="p"
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="companyName"
          rules={{
            required: "Company name is required"
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl>
              <TextField
                id="companyName"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                error={error?.message ? true : false}
                title="Company Name"
                variant="outlined"
                label="Company name"
                sx={{
                  width: "100%"
                }}
              />
              <ErrorMessage
                style={{
                  color: "red",
                  fontSize: "0.85rem",
                  marginTop: "0.375rem"
                }}
                errors={errors}
                name="companyName"
                as="p"
              />
            </FormControl>
          )}
        />
        <Controller
          control={control}
          name="companySize"
          rules={{
            required: "Company size is required"
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <FormControl>
              <TextField
                select
                id="companySize"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                error={error?.message ? true : false}
                title="Company Size"
                variant="outlined"
                label="Company size"
                sx={{
                  width: "100%"
                }}
              >
                {companySizeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <ErrorMessage
                style={{
                  color: "red",
                  fontSize: "0.85rem",
                  marginTop: "0.375rem"
                }}
                errors={errors}
                name="companySize"
                as="p"
              />
            </FormControl>
          )}
        />
      </Stack>
      <Stack
        direction="row"
        alignSelf={"center"}
        justifyContent="center"
        width="100%"
        gap={2}
      >
        {isLoading ? (
          <CircularProgress />
        ) : (
          <>
            <CustomButton onClick={handleFormSubmit} layout="primary">
              Request a Demo
            </CustomButton>
          </>
        )}
      </Stack>
    </Stack>
  );
}
